import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiCheckCircle, HiOutlineMail, HiXCircle } from "react-icons/hi";
import { FiUser, FiBriefcase } from "react-icons/fi";
import PhoneInput from "react-phone-input-2";

import { Button, Input, cn } from "../../ui";
import AuthShell from "./components/AuthShell";
import MessageBanner from "./components/MessageBanner";
import PasswordInput from "./components/PasswordInput";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/AdminContext";
import { defaultImage } from "../../lib/libraries/imageKit/ImageKit";
import { allowedCountries, countriesList } from "../components/data";

type PwdCheck = {
  hasLen6: boolean;
  hasLen10: boolean;
  hasLower: boolean;
  hasUpper: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
};

type PwdHints = {
  label: "Too weak" | "Fair" | "Good" | "Strong";
  score: number;
  checks: PwdCheck;
};

const STRENGTH_SEGMENTS = 5;

const getPasswordHints = (pwd = ""): PwdHints => {
  const checks: PwdCheck = {
    hasLen6: pwd.length >= 6,
    hasLen10: pwd.length >= 10,
    hasLower: /[a-z]/.test(pwd),
    hasUpper: /[A-Z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
    hasSymbol: /[^A-Za-z0-9]/.test(pwd),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const clamped = Math.min(STRENGTH_SEGMENTS, score);
  const label: PwdHints["label"] =
    clamped >= 4 ? "Strong" : clamped === 3 ? "Good" : clamped === 2 ? "Fair" : "Too weak";
  return { label, score: clamped, checks };
};

const HintItem: React.FC<{ ok: boolean; children: React.ReactNode }> = ({
  ok,
  children,
}) => (
  <li className="flex items-center gap-2 text-[12px]">
    {ok ? (
      <HiCheckCircle className="h-4 w-4 text-brand" />
    ) : (
      <HiXCircle className="h-4 w-4 text-gray-300" />
    )}
    <span className={ok ? "text-gray-700" : "text-gray-500"}>{children}</span>
  </li>
);

const FIELD_LABEL = "mb-1.5 block text-[12.5px] font-medium text-gray-700";
const FIELD_HELP = "mt-1.5 text-[11px] text-gray-500";

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { adminSignUp, sysAdmin } = useAdminContext();

  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phonenumber, setPhonenumber] = useState("");
  const [country, setCountry] = useState("Uganda");
  const [countryCode, setCountryCode] = useState("ug");
  const [password, setPassword] = useState("");
  const [cPassword, setCPassword] = useState("");

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const respond = (err: boolean, msg: string) => {
    setError(err);
    setSuccess(!err);
    setLoading(false);
    setMessage(msg);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(false);

    if (!name) return respond(true, "Company name is required");
    if (!firstName) return respond(true, "First name is required");
    if (!lastName) return respond(true, "Last name is required");
    if (!email) return respond(true, "Email is required");
    if (!phonenumber) return respond(true, "Phone number is required");
    if (!country) return respond(true, "Country is required");
    if (!password) return respond(true, "Password is required");
    if (password.length < 6) return respond(true, "Min. password size is 6");
    if (password !== cPassword) return respond(true, "Passwords do not match");

    try {
      const { message: m, status } = await adminSignUp({
        name,
        thumbnail: defaultImage,
        description: name,
        email,
        first_name: firstName,
        last_name: lastName,
        password,
        phonenumber,
        country,
      });
      if (status === 200 || status === 201) {
        navigate("/check-email", { state: { email } });
      } else {
        respond(true, m);
      }
    } catch {
      respond(true, "An unexpected error occurred.");
    }
  };

  const pwdHints = useMemo(() => getPasswordHints(password), [password]);
  const passwordsMatch =
    password.length > 0 && cPassword.length > 0 && password === cPassword;

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <AuthShell eyebrow="Create account" formWidth="lg">
      <div className="mb-6 flex items-center justify-center lg:hidden">
        <img src={dosee_logo} alt="DoseeAfrica" className="h-10 w-auto" />
      </div>

      <header className="mb-8">
        <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-gray-900">
          Create your account
        </h1>
        <p className="mt-2 text-[13.5px] text-gray-500">
          A few details and your company account is ready to go.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="su-company" className={FIELD_LABEL}>
            Company name
          </label>
          <Input
            id="su-company"
            type="text"
            value={name}
            autoComplete="organization"
            placeholder="Legal company name"
            leadingIcon={<FiBriefcase className="h-4 w-4" />}
            onChange={(e) => setName(e.target.value)}
          />
          <p className={FIELD_HELP}>
            Appears on invoices and your public profile.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="su-first" className={FIELD_LABEL}>
              First name
            </label>
            <Input
              id="su-first"
              type="text"
              value={firstName}
              autoComplete="given-name"
              placeholder="First name"
              leadingIcon={<FiUser className="h-4 w-4" />}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="su-last" className={FIELD_LABEL}>
              Last name
            </label>
            <Input
              id="su-last"
              type="text"
              value={lastName}
              autoComplete="family-name"
              placeholder="Last name"
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="su-email" className={FIELD_LABEL}>
            Email address
          </label>
          <Input
            id="su-email"
            type="email"
            value={email}
            autoComplete="email"
            placeholder="you@company.com"
            leadingIcon={<HiOutlineMail className="h-4 w-4" />}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className={FIELD_HELP}>
            We’ll send a verification code here to activate your account.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="su-country" className={FIELD_LABEL}>
              Country
            </label>
            <div className="relative">
              <select
                id="su-country"
                value={country}
                onChange={(e) => {
                  const val = e.target.value;
                  const picked = countriesList?.find((c: any) => c.name === val);
                  if (picked) {
                    setCountry(picked.name);
                    setCountryCode(picked.code);
                  } else {
                    setCountry("Uganda");
                    setCountryCode("ug");
                  }
                }}
                className="h-10 w-full appearance-none rounded-xl border border-gray-200 bg-white pl-3 pr-9 text-sm text-gray-900 shadow-sm transition-colors focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              >
                <option value="Select Country">Select country</option>
                {countriesList?.map((c: any) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                ▾
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="su-phone" className={FIELD_LABEL}>
              Telephone
            </label>
            <PhoneInput
              country={countryCode}
              preferredCountries={["ug", "cd", "ke"]}
              onlyCountries={allowedCountries}
              enableSearch
              autoFormat
              disableDropdown
              value={phonenumber}
              enableLongNumbers={false}
              onChange={(phone: string) => setPhonenumber(phone)}
              inputProps={{ id: "su-phone" }}
              containerClass="!w-full"
              inputClass="!w-full !h-10 !text-sm !rounded-xl !border-gray-200 !shadow-sm focus:!border-brand"
              buttonClass="!border-gray-200 !bg-gray-50 !rounded-l-xl"
            />
          </div>
        </div>

        <div>
          <label htmlFor="su-password" className={FIELD_LABEL}>
            Password
          </label>
          <PasswordInput
            id="su-password"
            value={password}
            autoComplete="new-password"
            placeholder="Create a password"
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
              setSuccess(false);
            }}
          />

          <div className="mt-2.5 flex items-center gap-1" aria-hidden>
            {Array.from({ length: STRENGTH_SEGMENTS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1 w-full rounded-full transition-colors",
                  i < pwdHints.score ? "bg-brand" : "bg-gray-200",
                )}
              />
            ))}
          </div>
          {password ? (
            <p className="mt-1.5 text-[11px] text-gray-500">
              Password strength:{" "}
              <span className="font-medium text-gray-700">
                {pwdHints.label}
              </span>
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-gray-500">
              Must be at least 6 characters.
            </p>
          )}

          <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5">
            <HintItem ok={pwdHints.checks.hasLen6}>At least 6 characters</HintItem>
            <HintItem ok={pwdHints.checks.hasLen10}>10+ (recommended)</HintItem>
            <HintItem ok={pwdHints.checks.hasLower}>Lowercase letter</HintItem>
            <HintItem ok={pwdHints.checks.hasUpper}>Uppercase letter</HintItem>
            <HintItem ok={pwdHints.checks.hasNumber}>Number</HintItem>
            <HintItem ok={pwdHints.checks.hasSymbol}>Symbol</HintItem>
          </ul>
        </div>

        <div>
          <label htmlFor="su-confirm" className={FIELD_LABEL}>
            Confirm password
          </label>
          <PasswordInput
            id="su-confirm"
            value={cPassword}
            autoComplete="new-password"
            placeholder="Re-enter password"
            onChange={(e) => {
              setCPassword(e.target.value);
              setError(false);
              setSuccess(false);
            }}
          />
          {cPassword ? (
            <div
              className={cn(
                "mt-2 flex items-center gap-1.5 text-[12px]",
                passwordsMatch ? "text-brand" : "text-red-600",
              )}
            >
              {passwordsMatch ? (
                <HiCheckCircle className="h-4 w-4" />
              ) : (
                <HiXCircle className="h-4 w-4" />
              )}
              {passwordsMatch ? "Passwords match" : "Passwords do not match"}
            </div>
          ) : null}
        </div>

        <MessageBanner
          error={error}
          success={success}
          message={message}
          onDismiss={() => {
            setError(false);
            setSuccess(false);
          }}
        />

        <Button
          type={loading ? "button" : "submit"}
          variant="primary"
          className="w-full"
          disabled={loading}
        >
          {loading ? (
            <AiOutlineLoading className="h-4 w-4 animate-spin" />
          ) : (
            "Create account"
          )}
        </Button>

        <p className="text-[11px] leading-relaxed text-gray-500">
          By creating an account, you agree to our
          <Link to="/terms" className="mx-1 text-gray-700 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-700">
            Terms of Use
          </Link>
          and
          <Link to="/privacy" className="ml-1 text-gray-700 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-700">
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-5 text-[12px]">
        <Link to="/login" className="font-medium text-brand hover:text-[#007a7a]">
          ← Already have an account? Sign in
        </Link>
      </div>
    </AuthShell>
  );
};

export default SignUp;
